import { useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useIntl } from 'react-intl'
import { FormikHelpers } from 'formik'
import type { SortingRule } from 'react-table'
import { mdiPlus } from '@mdi/js'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  domainUsersListActions,
  fetchDomainUsersListThunk,
  postUserToDomainThunk,
} from 'features/team/slice'
import {
  domainUsersListSelector,
  domainWithUsersSelector,
} from 'features/team/selectors'
import usePermissions from 'features/auth/permissions/usePermissions'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import useCurrentSelectedDomainId from 'features/DomainsSelector/hooks'
import SuccessModal from 'components/Modal/SuccessModal'
import { RegularModerate } from 'components/Typography'
import { AddUserModalFormValues, MappedDomainUser } from 'features/team/types'
import { PostUserToDomainPayload } from 'features/team/api'
import { CommonSearch, MobileSortBy } from 'components/Table'
import { getSortOptions } from 'components/Table/utils'
import { SpinnerCentered } from 'components/Loader'
import Permissions from 'features/auth/permissions'
import { DOMAIN_ADMIN } from 'features/auth/permissions/consts'
import { domainToDomainDataCacheSelector } from 'features/domains/selectors'
import { domainNestingEnabledSelector } from 'features/auth/permissions/selectors'
import { DomainsSelector } from 'features/DomainsSelector'
import { selectedNavDomainActions } from 'features/domains/slice'
import TeamTable from './components/TeamTable'
import AddUserModal from 'features/team/components/AddUserModal'
import { teamPageMessages } from './messages'
import { commonMessages } from 'app/i18n/messages'
import { TEAM_HEADERS } from './consts'
import {
  LeftSide,
  RowsCount,
  StyledAddUserDesktopButton,
  StyledAddUserMobileButton,
  StyledFilters,
  StyledNoTeam,
  StyledTeamH4,
  StyledTeamsContainer,
  StyledTeamsHeader,
  StyledTitleContainer,
  TeamIcon,
  TeamSearchDesktopWrapper,
  TeamSearchMobileWrapper,
} from './styles'

function TeamPage() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const hasDomainNesting = useAppSelector(domainNestingEnabledSelector)
  const domainId = useCurrentSelectedDomainId()

  const [hasDomainAdminPermission] = usePermissions({
    allow: [DOMAIN_ADMIN],
    domainId,
  })

  const domainUsersList = useAppSelector(domainUsersListSelector)
  const usersCount = Array.isArray(domainUsersList) ? domainUsersList.length : 0
  const isFetching = useAppSelector(domainWithUsersSelector.isFetching)
  const isFinished = useAppSelector(domainWithUsersSelector.isFinished)
  const isError = useAppSelector(domainWithUsersSelector.error)
  const domainsCache = useAppSelector(domainToDomainDataCacheSelector)

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [isAddUserSuccessModalOpen, setIsAddUserSuccessModalOpen] =
    useState(false)
  const [addedUserToDomainEmail, setAddedUserToDomainEmail] = useState('')

  const selectedDomainName = domainsCache[domainId]?.name || ''

  const [searchValue, setSearchValue] = useState<string>('')

  useEffect(() => {
    if (domainId) {
      dispatch(fetchDomainUsersListThunk(domainId))
    }
  }, [dispatch, domainId])

  useEffect(() => {
    // clear fetched users and selected sub-domain
    return () => {
      dispatch(domainUsersListActions.clear())
      dispatch(selectedNavDomainActions.clear())
    }
  }, [dispatch])

  const handleAddUserFormSubmit = async (
    values: AddUserModalFormValues,
    { setSubmitting, setStatus }: FormikHelpers<AddUserModalFormValues>
  ) => {
    try {
      setSubmitting(true)
      const payload: PostUserToDomainPayload = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        configAuthor: hasDomainNesting ? values.config : true, // config access. Always true without domain nesting.
        domainPermissionUser: values.admin,
        domainPermissionDomain: values.admin,
        homePermissionSsh: true, // From old SCM: Always give dealers SSH access. In the future we may wish to restrict this.
      }
      await dispatch(postUserToDomainThunk(domainId, payload))
      await dispatch(fetchDomainUsersListThunk(domainId))
      setAddedUserToDomainEmail(values.email)
      setSubmitting(false)
      setIsAddUserModalOpen(false)
      setIsAddUserSuccessModalOpen(true)
    } catch (err) {
      setStatus({ isError: true })
      setSubmitting(false)
      console.log(
        `Error adding user with email ${values.email} to domain ${domainId}`,
        err
      )
    }
  }

  const headers = useMemo(
    () => ({
      avatar: {
        label: '',
        id: TEAM_HEADERS.AVATAR,
      },
      name: {
        label: formatMessage(teamPageMessages.nameColumn),
        id: TEAM_HEADERS.NAME,
      },
      email: {
        label: formatMessage(teamPageMessages.emailColumn),
        id: TEAM_HEADERS.EMAIL,
      },
      admin: {
        label: formatMessage(teamPageMessages.adminColumn),
        id: TEAM_HEADERS.IS_ADMIN,
      },
      config: {
        label: formatMessage(teamPageMessages.configColumn),
        id: TEAM_HEADERS.IS_CONFIG_AUTHOR,
      },
      subscriptionManagement: {
        label: formatMessage(teamPageMessages.subscriptionManagementColumn),
        id: TEAM_HEADERS.IS_SUBSCRIPTION_MANAGER,
      },
      userId: {
        label: '',
        id: TEAM_HEADERS.ID,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(
    () =>
      getSortOptions(headers).filter(
        (option) =>
          ![
            TEAM_HEADERS.AVATAR,
            TEAM_HEADERS.ID,
            TEAM_HEADERS.IS_SUBSCRIPTION_MANAGER,
            TEAM_HEADERS.IS_CONFIG_AUTHOR,
            TEAM_HEADERS.IS_ADMIN,
          ].includes(option.id as TEAM_HEADERS)
      ),
    [headers]
  )
  const [sortBy, setSortBy] = useState<SortingRule<MappedDomainUser>>(
    sortOptions[0]
  )

  return (
    <>
      <StyledTeamsContainer>
        <StyledTeamsHeader>
          <StyledFilters>
            {hasDomainNesting && <DomainsSelector />}
            <TeamSearchDesktopWrapper>
              <CommonSearch
                setSearchValue={setSearchValue}
                placeholder={formatMessage(teamPageMessages.searchPlaceholder)}
                disableQuerySearch
                externalSearchValue={searchValue}
              />
            </TeamSearchDesktopWrapper>
          </StyledFilters>

          <StyledTitleContainer>
            <LeftSide>
              {selectedDomainName && (
                <>
                  <StyledTeamH4>{selectedDomainName}</StyledTeamH4>
                  <RowsCount>{`${usersCount} ${formatMessage(
                    teamPageMessages.usersLabel
                  )}`}</RowsCount>
                </>
              )}
            </LeftSide>

            <Permissions allow={[DOMAIN_ADMIN]} domainId={domainId}>
              <StyledAddUserDesktopButton
                variant="secondary"
                onClick={() => setIsAddUserModalOpen(true)}
              >
                {formatMessage(teamPageMessages.addUserButtonLabel)}
              </StyledAddUserDesktopButton>
              <StyledAddUserMobileButton
                onClick={() => setIsAddUserModalOpen(true)}
              >
                <TeamIcon path={mdiPlus} size={1} />
              </StyledAddUserMobileButton>
            </Permissions>
          </StyledTitleContainer>

          <TeamSearchMobileWrapper>
            <CommonSearch
              setSearchValue={setSearchValue}
              placeholder={formatMessage(teamPageMessages.searchPlaceholder)}
            />
          </TeamSearchMobileWrapper>
        </StyledTeamsHeader>

        <MobileSortBy<MappedDomainUser>
          headers={headers}
          sortOptions={sortOptions}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />

        {isFetching && !isFinished && <SpinnerCentered />}

        {isError && (
          <StyledNoTeam>
            {formatMessage(commonMessages.commonError)}
          </StyledNoTeam>
        )}

        {!isFetching && isEmpty(domainUsersList) && !isError && (
          <StyledNoTeam>
            {formatMessage(teamPageMessages.noUsersFoundMessage)}
          </StyledNoTeam>
        )}

        {isFinished && !isEmpty(domainUsersList) && !isError && (
          <CurrentUserPermissionsContext.Provider
            value={{ hasDomainAdminPermission }}
          >
            <TeamTable
              data={domainUsersList}
              searchValue={searchValue}
              headers={headers}
              defaultSortBy={sortOptions[0]}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          </CurrentUserPermissionsContext.Provider>
        )}
      </StyledTeamsContainer>

      {isAddUserModalOpen && (
        <AddUserModal
          handleSubmit={handleAddUserFormSubmit}
          selectedDomainName={selectedDomainName}
          domainUsersList={domainUsersList}
          setIsAddUserModalOpen={setIsAddUserModalOpen}
          hasDomainNesting={hasDomainNesting}
        />
      )}
      {isAddUserSuccessModalOpen && (
        <SuccessModal
          isOpen
          confirmBtnTitle={formatMessage(commonMessages.done)}
          title={formatMessage(commonMessages.success)}
          onConfirm={() => setIsAddUserSuccessModalOpen(false)}
        >
          <RegularModerate>
            {formatMessage(teamPageMessages.successModalMessage, {
              email: addedUserToDomainEmail,
              selectedDomainName,
            })}
          </RegularModerate>
        </SuccessModal>
      )}
    </>
  )
}

export default TeamPage
